import { useContext, useEffect, useCallback, useMemo } from 'react';
import { BIMContext } from 'providers/bim-provider';
import { TitleSection, CardComponent, SelectMenu, Textarea, CardRow, Docker, Textbox, BreadCrumb, TabList, Popup, ButtonRow, BottomSection } from 'components/generic';
import { BIMTabList, dockerlist, BIMSectionList, conditionSearchList, bimFileType } from 'config';
import { GeneralDetails, UploadData, AlertMessage, Linkage } from './components';
import { getValuesById, requestValidator } from 'utils';
import Images from 'assets/Images';
import { useFetch } from 'hooks';
import { AnimatePresence, motion } from 'framer-motion';
import './style.scss';

const CustomerUpload = () => {
	const context = useContext(BIMContext);
	const api = useFetch();
	const {
		createObj,
		dockerState,
		errorMsgLoadCode,
		savePopupState,
		addValidation,
		setAddValidation,
		setSavePopupState,
		activeSection,
		addPopupToggler,
		setAddPopupToggler,
		setSelectedMapping,
		setErrorMsgLoadCode,
		setDockerState,
		selectedMapping,
		reset
	} = context;

	useEffect(() => {
		setAddValidation(requestValidator({ ...createObj, validatorKey: 'bimAdd' }));
	}, [createObj]);

	// batchFileMapping for listing API Call
	const batchFileMapping = async obj => {
		const bimListing = { start_index: 0, record_count: -1, filter: obj?.filter || [], sort: obj?.sort || [], searchValue: obj?.searchValue, list_type: [] };
		const { data } = await api({ url: 'batchFileMapping/v1', method: 'post', data: bimListing });
		const list = data?.data[0]?.result.map(el => ({
			title: el?.bfm_loadCode,
			description: el?.bfm_description,
			status: el?.bfm_status,
			image: el?.bfm_fileType,
			updatedAt: el?.updatedAt,
			el: el,
			value: el?.bfm_loadCode
		}));
		context.setListingData(list);
		reset({ createObj: true });
	};

	// check load code API Call
	const checkLoadCodeValidation = async e => {
		if (e) {
			const { data } = await api({ url: 'batchFileMapping/v1/checkLoadCode/' + e.replaceAll(' ', '%20'), method: 'get' });
			setErrorMsgLoadCode(!data?.data?.valid ? data?.data?.message : null);
		}
	};

	// Add item to BIM API Call
	const handleMappingSave = async () => {
		await api({ url: `batchFileMapping/v1/${dockerState === 'edit' ? 'edit' : 'add'}`, method: dockerState === 'edit' ? 'patch' : 'post', data: createObj, successToaster: true });
		batchFileMapping();
	};

	// deleteMapping from BIM list API Call
	const deleteMapping = async () => {
		await api({ url: 'batchFileMapping/v1/delete', method: 'delete', data: { bfm_loadCode: selectedMapping.title }, successToaster: true });
		batchFileMapping();
	};

	// handle listing checkbox
	const handleCheckbox = (e, data) => {
		if (e) {
			context.setCreateObj(data.el);
			setSelectedMapping(data);
		} else {
			reset({ createObj: true });
		}
	};

	// docker click handler
	const handleDocker = e => {
		e?.key === 'cancel' ? reset({ createObj: true }) : setDockerState(e?.key);
		e?.key === 'edit' && context.setTrailFormulaData(e?.list[0]?.el?.bfm_fileObj ? JSON.parse(e?.list[0]?.el?.bfm_fileObj) : null);
	};

	// handle add button
	const handleButtonClick = e => {
		e === 'Goback' && reset({ createObj: true });
		if (e === 'Create') {
			setDockerState('add');
			setAddPopupToggler(false);
		}
	};

	// add, edit, view section click
	const handleSectionClick = e => {
		context.setActiveSection(e?.key);
	};

	// data handler to create obj
	const dataHandler = (e, key) => {
		context.setCreateObj(prevState => ({ ...prevState, [key]: e }));
	};

	// add, edit, view section to sumbit or  to Goback
	const handleBottomSection = e => {
		setSavePopupState(e);
	};

	useEffect(() => {
		batchFileMapping();
	}, []);

	// listing cards
	const cardsDisplay = useMemo(() => {
		return context.listingData?.map((el, i) => <CardComponent key={i} item={el} checkbox checked={selectedMapping?.title === el?.title} handleChecked={e => handleCheckbox(e, el)} />);
	}, [context.listingData, selectedMapping]);

	// add popup form
	const addItem = (
		<>
			<h3>Create Integration Maintenance</h3>
			<Textbox
				label='Mapping Name'
				infoContent='Enter Mapping Name'
				placeholder='ADD INFO'
				error={errorMsgLoadCode}
				errorMessage={errorMsgLoadCode}
				value={createObj?.bfm_loadCode}
				mandatory
				onChange={e => dataHandler(e?.target?.value, 'bfm_loadCode')}
				onBlur={e => checkLoadCodeValidation(e?.target?.value)}
			/>
			<Textarea label='Mapping Description' infoContent='Enter Mapping Description' placeholder='Additional Fields for Customer Information' value={createObj?.bfm_description} mandatory onChange={e => dataHandler(e?.target?.value, 'bfm_description')} />
			<SelectMenu label='File Type' infoContent='Select File Type' menuOptions={bimFileType} placeholder='CSV' mandatory value={getValuesById(bimFileType, createObj?.bfm_fileType)} onChange={e => dataHandler(e, 'bfm_fileType')} />
		</>
	);

	// handler for add popup item
	const addToggler = () => {
		reset({ createObj: true });
		setAddPopupToggler(true);
	};

	// handler for delete item
	const handleDeleteButton = e => {
		e === 'Yes' && deleteMapping();
		e === 'No' && reset({ createObj: true });
	};

	// popup confirmation on save and goback
	const handleSaveClickPopup = useCallback(
		e => {
			e === 'Yes' && savePopupState === 'Save' && handleMappingSave();
			e === 'Yes' && savePopupState === 'Goback' && reset({ createObj: true });
			e === 'No' && savePopupState === 'Save' && setSavePopupState(null);
			e === 'No' && savePopupState === 'Goback' && setSavePopupState(null);
		},
		[savePopupState]
	);

	// handler for save and delete popup confirmations
	const handleSaveDeletePopup = (e, type) => {
		type === 'delete' && handleDeleteButton(e);
		type === 'save' && handleSaveClickPopup(e);
	};

	// popup content
	const createPopupContent = useMemo(() => {
		return (
			<Popup onClose={() => setAddPopupToggler(false)} popupState={addPopupToggler}>
				<div className='bim__addpopup'>
					{addItem}
					<ButtonRow label='Goback' label1='Create' right disableSave={!createObj?.bfm_loadCode || !createObj?.bfm_description || !createObj?.bfm_fileType || errorMsgLoadCode} handleButtonClick={handleButtonClick}></ButtonRow>
				</div>
			</Popup>
		);
	}, [addPopupToggler, createObj, errorMsgLoadCode]);

	// popup confirmation content for save and delete
	const saveDeletePopupContent = ({ label, type }) => {
		return (
			<div className='bim__confirmation'>
				<img src={Images.warningPopup} alt='warning' />
				<h3>Warning</h3>
				<p>{label}</p>
				<ButtonRow label='No' label1='Yes' handleButtonClick={e => handleSaveDeletePopup(e, type)} />
			</div>
		);
	};

	const closePopup = type => {
		setSavePopupState(null);
		if (type === 'delete') {
			setSelectedMapping(null);
			setDockerState('listing');
		}
	};

	// popup content for delete
	const deletePopup = useMemo(() => {
		return (
			<Popup onClose={() => closePopup('delete')} popupState={dockerState === 'delete'}>
				{saveDeletePopupContent({ label: 'Are you sure you want to delete?', type: 'delete' })}
			</Popup>
		);
	}, [dockerState]);

	// popup content for going back and save
	const gobackSavePopup = useMemo(() => {
		return (
			<Popup onClose={() => closePopup('save')} popupState={savePopupState?.length > 0}>
				{saveDeletePopupContent({ label: savePopupState === 'Save' ? 'Are you sure you want to save?' : 'Do you want to go back to dashboard?', type: 'save' })}
			</Popup>
		);
	}, [savePopupState]);

	// display the active section
	const displayActiveSection = useCallback(() => {
		const obj = {
			general_details: () => <GeneralDetails checkLoadCode={checkLoadCodeValidation} />,
			upload_data: () => <UploadData />,
			linkage: () => <Linkage />,
			alert_messages: () => <AlertMessage />,
			DEFAULT: () => null
		};
		return (obj[activeSection] || obj['DEFAULT'])();
	}, [activeSection]);

	// titleSection tab click
	const handleTitleSection = e => {
		let obj = {};
		if (e?.filter) {
			obj['filter'] = [{ id: e?.id, value: e?.key }];
		} else {
			obj['sort'] = [{ id: e?.key, value: -1 }];
		}
		context.setActiveTitle(e?.key);
		batchFileMapping(obj);
	};

	// handle title search
	const handleTitleSearch = e => {
		batchFileMapping({ filter: e?.obj ? [e?.obj] : [] });
	};

	return (
		<>
			{['listing', 'delete'].includes(dockerState) && (
				<>
					<TitleSection
						tab
						add
						search
						title='Integration Maintenance'
						count={context?.listingData?.length}
						headerList={BIMTabList}
						handleTabClick={handleTitleSection}
						activeTab={context.activeTitle}
						handleAdd={addToggler}
						handleSearch={handleTitleSearch}
						searchList={conditionSearchList}
					/>

					<motion.div className='customer scroll-bar' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.1 }}>
						<CardRow>{cardsDisplay}</CardRow>
					</motion.div>

					{deletePopup}
					{createPopupContent}
					<AnimatePresence>
						{selectedMapping && (
							<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.1 }}>
								<Docker dockerClick={handleDocker} dockerlist={dockerlist} selectlist={[selectedMapping]} />
							</motion.div>
						)}
					</AnimatePresence>
				</>
			)}
			{!['listing', 'delete'].includes(dockerState) && (
				<>
					<BreadCrumb path={['Batch Integration Maintenance', dockerState, activeSection]} />
					<TabList tabList={BIMSectionList} tabActive={activeSection} handleTabClick={handleSectionClick} />
					<BottomSection handleButtonClick={handleBottomSection} disableSave={dockerState === 'view' || addValidation || !createObj?.bfm_linkages[0].p_code || !createObj?.bfm_linkages[0].bfm_filePath} />
					{gobackSavePopup}
					<AnimatePresence mode='wait'>
						<motion.div key={activeSection} initial={{ x: -100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 100, opacity: 0 }} transition={{ duration: 0.1 }}>
							{displayActiveSection()}
						</motion.div>
					</AnimatePresence>
				</>
			)}
		</>
	);
};

export default CustomerUpload;
